import React, { useEffect, useState } from 'react';
import { Loader2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileCategory } from '@/types';
import { cn } from '@/lib/utils';

interface CategoryOption {
  value: FileCategory;
  label: string;
}

interface ChangeCategoryDialogProps {
  open: boolean;
  /** Number of selected files — shown in the description. */
  count: number;
  /** Category to preselect (usually the current tab). */
  currentCategory?: FileCategory;
  options: CategoryOption[];
  isSaving?: boolean;
  onCancel: () => void;
  onSave: (category: FileCategory) => void;
}

export default function ChangeCategoryDialog({
  open,
  count,
  currentCategory,
  options,
  isSaving = false,
  onCancel,
  onSave,
}: ChangeCategoryDialogProps) {
  const [selected, setSelected] = useState<FileCategory | undefined>(currentCategory);

  // Re-seed the selection whenever the dialog opens / targets a different current category.
  useEffect(() => {
    if (open) setSelected(currentCategory);
  }, [open, currentCategory]);

  const handleSave = () => {
    if (isSaving || !selected) return;
    onSave(selected);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent dir="rtl" className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-right">שנה סיווג</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground text-right">
          בחר סיווג עבור {count} קבצים שנבחרו
        </p>

        <div className="space-y-2">
          {options.map((opt) => {
            const isActive = selected === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelected(opt.value)}
                disabled={isSaving}
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50',
                  isActive
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-card border-border hover:bg-secondary/50'
                )}
              >
                <span>{opt.label}</span>
                {isActive && <Check className="w-4 h-4" />}
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            ביטול
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !selected}>
            {isSaving && <Loader2 className="w-4 h-4 animate-spin ml-2" />}
            שמור
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
